import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import { CameraIcon } from '@synoptic/ui-kit/icons/react/camera.tsx';
import { WarningCircleIcon } from '@synoptic/ui-kit/icons/react/warning-circle.js';
import { XmarkIcon } from '@synoptic/ui-kit/icons/react/xmark.js';
import { VisuallyHidden } from '@synoptic/ui-kit/visually-hidden/visually-hidden.tsx';
import { FC, ReactNode, useMemo } from 'react';
import { Delayed } from '../delayed.tsx';
import {
  uploadPreviewError,
  uploadPreviewLoader,
} from '../upload-preview/upload-preview.css.ts';
import {
  actionsContainer,
  imageUploadPreviewContainer,
} from './image-upload-preview.css.ts';

interface AvatarUploadPreviewProps {
  file: File;
  state: 'error' | 'loading' | 'success';
  onRemove: () => void;
  progress?: number;
  setFile: (file: File) => void;
  renderPreview: (previewSrc?: string) => ReactNode;
  borderRadius?: number;
}

export const ImageUploadPreview: FC<AvatarUploadPreviewProps> = ({
  state,
  progress,
  file,
  onRemove,
  setFile,
  renderPreview,
  borderRadius,
}) => {
  // URL.createObjectURL(file) generates new url every render which caused flickering on videos
  const previewSrc = useMemo(() => URL.createObjectURL(file), [file]);

  return (
    <div style={{ borderRadius }} className={imageUploadPreviewContainer}>
      {state === 'loading' ? (
        <Delayed delayMs={3000}>
          <div style={{ borderRadius }} className={uploadPreviewLoader}>
            Uploading...
            {progress ? `${progress}%` : ''}
          </div>
        </Delayed>
      ) : null}

      {state === 'error' ? (
        <div style={{ borderRadius }} className={uploadPreviewError}>
          <WarningCircleIcon width={16} height={16} />
        </div>
      ) : null}

      <div className={actionsContainer}>
        <IconButton variant="secondary" size="small" asChild>
          <label>
            <CameraIcon />
            <VisuallyHidden>
              <input
                onChange={(e) => {
                  onRemove();
                  if (e.target.files) {
                    setFile(e.target.files[0]);
                  }
                }}
                type="file"
                accept="image/*"
              />
            </VisuallyHidden>
          </label>
        </IconButton>
        <IconButton variant="secondary" onClick={onRemove} size="small">
          <XmarkIcon />
        </IconButton>
      </div>

      {renderPreview(previewSrc)}
    </div>
  );
};
